import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import { ui } from '../ui';
import { formatDateTimeLocal } from '../format';
import { StatusBadge } from '../../../components/common/StatusBadge';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import {
  SweepRun, CommunicationSettings,
  getSweepStatus, getSettings, updateSettings, runSweepBackfill,
} from '../api';

// Client-side pre-check bounds — the server remains the authority (docs/requirements/
// zoom-api-sweep.md §6); these just give the admin an immediate answer before a round trip.
const INTERVAL_MIN_MINUTES = 5;
const INTERVAL_MAX_MINUTES = 1440;
const LOOKBACK_MIN_HOURS = 1;
const LOOKBACK_MAX_HOURS = 168;
// How often to poll GET .../sweep/status while a backfill run is in progress server-side
// (the POST returns 202 immediately per the async design; see runSweepBackfill in api.ts).
const BACKFILL_POLL_MS = 5_000;

// Surfaces the server's 400/409 { error } message verbatim; falls back to a generic
// retry prompt for network failures / 500s where there's no such body (mirrors
// TimeTrackingUploads' extractErrorMessage).
const extractErrorMessage = (err: unknown, fallback: string): string => {
  if (isAxiosError(err)) {
    const data = err.response?.data as { error?: string } | undefined;
    if (data?.error) return data.error;
  }
  return fallback;
};

// Local calendar date (viewer's own timezone) as YYYY-MM-DD, for the backfill date inputs'
// `max` attribute and the "to must not be in the future" pre-check.
const localTodayIso = (): string => {
  const d = new Date();
  const offsetMs = d.getTimezoneOffset() * 60_000;
  return new Date(d.getTime() - offsetMs).toISOString().slice(0, 10);
};
// Six calendar months before today (Zoom's stated call-history retention floor, §11).
const sixMonthsAgoIso = (): string => {
  const d = new Date();
  d.setMonth(d.getMonth() - 6);
  const offsetMs = d.getTimezoneOffset() * 60_000;
  return new Date(d.getTime() - offsetMs).toISOString().slice(0, 10);
};

// Renders one sweep run's counts/status — shared by the "Last Sweep" panel and the
// historical backfill's result summary so the two look consistent.
const SweepRunSummary: React.FC<{ run: SweepRun }> = ({ run }) => (
  <div className="rounded-md border border-border-default bg-surface p-4 text-sm">
    <div className="mb-2 flex flex-wrap items-center gap-2">
      <span className={run.Status === 'Running' ? 'animate-pulse' : undefined}>
        <StatusBadge status={run.Status} />
      </span>
      <span className="text-muted">Started {formatDateTimeLocal(run.StartedUtc)}</span>
      {run.FinishedUtc && (
        <span className="text-muted">· Finished {formatDateTimeLocal(run.FinishedUtc)}</span>
      )}
    </div>
    <div className="flex flex-wrap gap-x-5 gap-y-1 text-secondary">
      <span>Calls seen: <span className="font-medium text-primary">{run.CallsSeen}</span></span>
      <span>Created: <span className="font-medium text-primary">{run.CallsCreated}</span></span>
      <span>Backfilled: <span className="font-medium text-primary">{run.CallsBackfilled}</span></span>
      <span>Errors: <span className={`font-medium ${run.Errors > 0 ? 'text-red-600' : 'text-primary'}`}>{run.Errors}</span></span>
    </div>
    {run.ErrorSummary && (
      <p className="mt-2 break-words text-amber-700 dark:text-amber-400">{run.ErrorSummary}</p>
    )}
  </div>
);

export const ZoomSweepPanel: React.FC = () => {
  // ── Last Sweep status ────────────────────────────────────────────────────
  const [status, setStatus] = useState<SweepRun | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [statusLoaded, setStatusLoaded] = useState(false);

  const loadStatus = useCallback(() => {
    setStatusLoading(true);
    setStatusError(null);
    return getSweepStatus()
      .then(s => setStatus(s))
      .catch(() => setStatusError('Could not load the last sweep status — try refreshing.'))
      .finally(() => { setStatusLoading(false); setStatusLoaded(true); });
  }, []);

  // ── Settings (interval / lookback / read-only enabled flag) ─────────────
  const [settings, setSettings] = useState<CommunicationSettings | null>(null);
  const [intervalInput, setIntervalInput] = useState('');
  const [lookbackInput, setLookbackInput] = useState('');
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsMsg, setSettingsMsg] = useState<string | null>(null);

  const loadSettings = useCallback(() => {
    return getSettings().then(s => {
      setSettings(s);
      setIntervalInput(String(s.SweepIntervalMinutes));
      setLookbackInput(String(s.SweepLookbackHours));
    });
  }, []);

  useEffect(() => {
    loadStatus();
    loadSettings();
  }, [loadStatus, loadSettings]);

  const intervalNum = Number(intervalInput);
  const lookbackNum = Number(lookbackInput);
  const intervalValid = intervalInput.trim() !== '' && Number.isFinite(intervalNum)
    && intervalNum >= INTERVAL_MIN_MINUTES && intervalNum <= INTERVAL_MAX_MINUTES;
  const lookbackValid = lookbackInput.trim() !== '' && Number.isFinite(lookbackNum)
    && lookbackNum >= LOOKBACK_MIN_HOURS && lookbackNum <= LOOKBACK_MAX_HOURS;

  const handleSaveSweepSettings = async () => {
    if (!intervalValid || !lookbackValid) return;
    setSettingsSaving(true);
    setSettingsMsg(null);
    try {
      const updated = await updateSettings({ SweepIntervalMinutes: intervalNum, SweepLookbackHours: lookbackNum });
      setSettings(updated);
      setIntervalInput(String(updated.SweepIntervalMinutes));
      setLookbackInput(String(updated.SweepLookbackHours));
      setSettingsMsg('Saved.');
    } catch {
      setSettingsMsg('Save failed.');
    } finally {
      setSettingsSaving(false);
    }
  };

  // ── Historical backfill ──────────────────────────────────────────────────
  // Async design: POST .../sweep/run returns 202 immediately with a SweepRun-shaped body
  // whose Status is 'Running' — the run continues server-side. backfillRun holds that
  // response, then each successive poll of getSweepStatus() while it's still 'Running',
  // ending with the terminal (OK/PartialError/Failed) row.
  const [backfillFrom, setBackfillFrom] = useState('');
  const [backfillTo, setBackfillTo] = useState('');
  const [backfillRunning, setBackfillRunning] = useState(false);
  const [backfillRun, setBackfillRun] = useState<SweepRun | null>(null);
  const [backfillError, setBackfillError] = useState<string | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current !== null) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  // One poll tick: fetches the latest status, updates both the backfill result area and
  // the "Last Sweep" panel (they're the same underlying row while a backfill is running),
  // and stops polling once the run reaches a terminal status.
  const pollBackfillStatus = useCallback(() => {
    getSweepStatus()
      .then(s => {
        if (s) {
          setBackfillRun(s);
          setStatus(s);
        }
        if (!s || s.Status !== 'Running') {
          stopPolling();
          setBackfillRunning(false);
        }
      })
      .catch(() => {
        // A transient poll failure shouldn't kill the in-progress indicator — just retry
        // on the next tick rather than surfacing a false failure mid-run.
      });
  }, [stopPolling]);

  const startPolling = useCallback(() => {
    stopPolling();
    pollTimerRef.current = setInterval(pollBackfillStatus, BACKFILL_POLL_MS);
  }, [stopPolling, pollBackfillStatus]);

  // Stop any in-flight poll loop if the panel unmounts mid-run.
  useEffect(() => stopPolling, [stopPolling]);

  const today = useMemo(() => localTodayIso(), []);
  const sixMonthsAgo = useMemo(() => sixMonthsAgoIso(), []);

  // Derived (not stored) so it's always in sync with the two date fields; null once both
  // fields are filled and the range is acceptable, or while either field is still empty.
  const backfillPreCheckError: string | null = (() => {
    if (!backfillFrom || !backfillTo) return null;
    if (backfillFrom > backfillTo) return 'Start date must be on or before the end date.';
    if (backfillTo > today) return 'End date cannot be in the future.';
    if (backfillFrom < sixMonthsAgo) return 'Start date cannot be more than six months ago — Zoom retains only six months of call history.';
    return null;
  })();
  const backfillReady = !!backfillFrom && !!backfillTo && !backfillPreCheckError;

  const handleRunBackfill = async () => {
    if (!backfillReady || backfillRunning) return;
    setBackfillRunning(true);
    setBackfillError(null);
    setBackfillRun(null);
    try {
      // Returns fast (202) with a 'Running' row — render it immediately, then poll for
      // the terminal result rather than waiting on this call.
      const initial = await runSweepBackfill(backfillFrom, backfillTo);
      setBackfillRun(initial);
      if (initial.Status === 'Running') {
        startPolling();
      } else {
        setBackfillRunning(false);
      }
    } catch (err) {
      setBackfillError(extractErrorMessage(err, 'Backfill failed — try again.'));
      // A 409 means a run IS already in progress (just not one this click started) — poll
      // anyway so the admin sees it through to completion instead of a dead-end error.
      if (isAxiosError(err) && err.response?.status === 409) {
        startPolling();
      } else {
        setBackfillRunning(false);
      }
    }
  };

  return (
    <section className={ui.sectionCard}>
      <h2 className={`${ui.sectionTitle} mb-1`}>Zoom API Sweep</h2>
      <p className="mb-4 text-sm text-muted">
        Reconciles Zoom call history against Communication.Call — recovering calls the webhook
        missed and backfilling duration/recording on existing rows without touching agent notes.
      </p>

      {/* ── Last Sweep status ────────────────────────────────────────────── */}
      <div className="mb-6">
        <div className="mb-2 flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-secondary">Last Sweep</h3>
          <button type="button" className={ui.btnSecondary} onClick={loadStatus} disabled={statusLoading}>
            {statusLoading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
        <div aria-live="polite">
          {statusLoading && <LoadingSpinner size="sm" message="Loading sweep status…" />}
          {!statusLoading && statusError && (
            <p role="alert" className="text-sm text-red-600">{statusError}</p>
          )}
          {!statusLoading && !statusError && statusLoaded && status === null && (
            <p className="text-sm text-muted">No sweep recorded.</p>
          )}
          {!statusLoading && !statusError && status && <SweepRunSummary run={status} />}
        </div>
      </div>

      {/* ── Settings: enabled flag (read-only) + interval + lookback ──────── */}
      <div className="mb-6">
        <h3 className="mb-2 text-sm font-semibold text-secondary">Sweep Schedule</h3>
        {settings && (
          <p className="mb-3 text-xs text-muted">
            <span
              className={`rounded px-1.5 py-0.5 ${
                settings.SweepEnabled
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                  : 'bg-surface-hover text-muted'
              }`}
            >
              {settings.SweepEnabled ? 'Enabled via server config' : 'Disabled — set ZoomApiSweep:Enabled'}
            </span>
          </p>
        )}
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className={ui.label} htmlFor="sweep-interval-minutes">Interval (minutes)</label>
            <input
              id="sweep-interval-minutes"
              type="number"
              min={INTERVAL_MIN_MINUTES}
              max={INTERVAL_MAX_MINUTES}
              className={ui.control}
              value={intervalInput}
              onChange={e => { setIntervalInput(e.target.value); setSettingsMsg(null); }}
            />
          </div>
          <div>
            <label className={ui.label} htmlFor="sweep-lookback-hours">Lookback (hours)</label>
            <input
              id="sweep-lookback-hours"
              type="number"
              min={LOOKBACK_MIN_HOURS}
              max={LOOKBACK_MAX_HOURS}
              className={ui.control}
              value={lookbackInput}
              onChange={e => { setLookbackInput(e.target.value); setSettingsMsg(null); }}
            />
          </div>
          <button
            type="button"
            className={ui.btnPrimary}
            onClick={handleSaveSweepSettings}
            disabled={settingsSaving || !intervalValid || !lookbackValid}
          >
            {settingsSaving ? 'Saving…' : 'Save'}
          </button>
          {settingsMsg && <span className="text-sm text-muted">{settingsMsg}</span>}
        </div>
        {!intervalValid && intervalInput.trim() !== '' && (
          <p role="alert" className="mt-1 text-xs text-red-600">
            Interval must be between {INTERVAL_MIN_MINUTES} and {INTERVAL_MAX_MINUTES} minutes.
          </p>
        )}
        {!lookbackValid && lookbackInput.trim() !== '' && (
          <p role="alert" className="mt-1 text-xs text-red-600">
            Lookback must be between {LOOKBACK_MIN_HOURS} and {LOOKBACK_MAX_HOURS} hours.
          </p>
        )}
      </div>

      {/* ── Historical backfill ─────────────────────────────────────────── */}
      <div>
        <h3 className="mb-1 text-sm font-semibold text-secondary">Run Historical Backfill</h3>
        <p className="mb-3 text-xs text-muted">
          Sweep an arbitrary past date range (e.g. to recover calls from before this feature went
          live). Zoom retains only six months of call history, so the start date can't be older
          than that.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className={ui.label} htmlFor="sweep-backfill-from">From</label>
            <input
              id="sweep-backfill-from"
              type="date"
              className={ui.control}
              value={backfillFrom}
              min={sixMonthsAgo}
              max={backfillTo || today}
              onChange={e => { setBackfillFrom(e.target.value); setBackfillError(null); setBackfillRun(null); }}
              disabled={backfillRunning}
            />
          </div>
          <div>
            <label className={ui.label} htmlFor="sweep-backfill-to">To</label>
            <input
              id="sweep-backfill-to"
              type="date"
              className={ui.control}
              value={backfillTo}
              min={backfillFrom || sixMonthsAgo}
              max={today}
              onChange={e => { setBackfillTo(e.target.value); setBackfillError(null); setBackfillRun(null); }}
              disabled={backfillRunning}
            />
          </div>
          <button
            type="button"
            className={ui.btnPrimary}
            onClick={handleRunBackfill}
            disabled={!backfillReady || backfillRunning}
          >
            {backfillRunning ? 'Running…' : 'Run'}
          </button>
        </div>

        <div aria-live="polite" className="mt-3 space-y-2">
          {backfillPreCheckError && (
            <p role="alert" className="text-sm text-red-600">{backfillPreCheckError}</p>
          )}
          {backfillError && (
            <p role="alert" className="text-sm text-red-600">{backfillError}</p>
          )}
          {backfillRun?.Status === 'Running' && (
            <LoadingSpinner
              size="sm"
              message={`Backfill running since ${formatDateTimeLocal(backfillRun.StartedUtc)}… (this can take a while for a wide date range)`}
            />
          )}
          {backfillRun && backfillRun.Status !== 'Running' && <SweepRunSummary run={backfillRun} />}
        </div>
      </div>
    </section>
  );
};
